import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { MessageCircle, X, Send } from "lucide-react";

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Hello, I'm Aria — your DobhaiOfficial concierge. How can I help with bookings, packages, or availability today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I'm offline for a moment. Please email hello@dobhaiofficial.com." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 bg-primary text-primary-foreground w-14 h-14 flex items-center justify-center shadow-lg hover:bg-[#2b3d2b] transition-colors"
        data-testid="chatbot-toggle"
        aria-label="Open chat"
      >
        {open ? <X size={22} strokeWidth={1.25} /> : <MessageCircle size={22} strokeWidth={1.25} />}
      </button>
      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[92vw] h-[520px] bg-card border border-border shadow-2xl flex flex-col"
          data-testid="chatbot-panel"
        >
          <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center justify-between">
            <div>
              <div className="font-serif text-lg leading-none">Aria</div>
              <div className="text-[10px] tracking-[0.25em] uppercase opacity-80">Studio Concierge</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#C9D1B5] animate-pulse" />
              <span className="text-xs opacity-80">Online</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/30">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                <div
                  className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-card border border-border text-foreground"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-card border border-border px-4 py-2.5 text-sm text-muted-foreground">
                  <span className="animate-pulse">Aria is typing…</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-border flex gap-2 bg-card">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about packages, dates…"
              className="flex-1 px-3 py-2 text-sm border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={send}
              disabled={loading}
              className="bg-primary text-primary-foreground px-3 disabled:opacity-60"
              data-testid="chatbot-send"
              aria-label="Send message"
            >
              <Send size={16} strokeWidth={1.25} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
